import React, {useEffect, useRef, useState} from "react";
import API from "../helpers/Axios";

const normalizeList = (payload) => {
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.data)) return payload.data;
	if (Array.isArray(payload?.items)) return payload.items;
	return [];
};

function AddStudioAdModal({
	isOpen,
	isSaving = false,
	onClose,
	onSubmit,
	modalTitle = "Add ad",
	modalDescription = "Create a new studio ad campaign.",
	submitLabel = "Save ad",
	serverErrors = {},
	onClearServerErrors,
	initialValues = null,
	requireCover = true,
}) {
	const [studioSearch, setStudioSearch] = useState("");
	const [studios, setStudios] = useState([]);
	const [selectedStudio, setSelectedStudio] = useState(null);
	const [isStudioSearching, setIsStudioSearching] = useState(false);
	const [isStudioDropdownOpen, setIsStudioDropdownOpen] = useState(false);

	const [slotSearch, setSlotSearch] = useState("");
	const [slots, setSlots] = useState([]);
	const [selectedSlots, setSelectedSlots] = useState([]);
	const [isSlotLoading, setIsSlotLoading] = useState(false);
	const [isSlotDropdownOpen, setIsSlotDropdownOpen] = useState(false);

	const [title, setTitle] = useState("");
	const [destinationUrl, setDestinationUrl] = useState("");
	const [coverFile, setCoverFile] = useState(null);
	const [coverName, setCoverName] = useState("");
	const [coverPreviewUrl, setCoverPreviewUrl] = useState("");
	const [priority, setPriority] = useState("10");
	const [sortOrder, setSortOrder] = useState("1");
	const [budgetImpressions, setBudgetImpressions] = useState("");
	const [budgetClicks, setBudgetClicks] = useState("");
	const [isActive, setIsActive] = useState(true);
	const [fieldErrors, setFieldErrors] = useState({});

	const studioDropdownRef = useRef(null);
	const slotDropdownRef = useRef(null);
	const coverPreviewObjectUrlRef = useRef("");

	const firstNonEmpty = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== "");
	const toStringOr = (value, fallback = "") => (value === undefined || value === null ? fallback : String(value));

	useEffect(() => {
		if (!isOpen) return;
		setStudioSearch("");
		setStudios([]);
		const presetStudioId = initialValues?.studio_id ?? initialValues?.studio?.id ?? null;
		const presetStudioName = firstNonEmpty(initialValues?.studio?.name, initialValues?.studio_name, initialValues?.studio_title);
		setSelectedStudio(presetStudioId ? {id: presetStudioId, name: presetStudioName || `Studio #${presetStudioId}`} : null);
		setStudioSearch(presetStudioName || "");
		setIsStudioSearching(false);
		setIsStudioDropdownOpen(false);
		setSlots([]);
		const presetSlotIds = Array.isArray(initialValues?.slot_ids)
			? initialValues.slot_ids
			: Array.isArray(initialValues?.slots)
				? initialValues.slots.map((slot) => slot.id)
				: initialValues?.slot_id
					? [initialValues.slot_id]
					: initialValues?.ad_slot_id
						? [initialValues.ad_slot_id]
						: [];
		const presetSlotsFromObjects = Array.isArray(initialValues?.slots)
			? initialValues.slots
					.map((slot) => ({id: slot.id, name: firstNonEmpty(slot.name, slot.key, `Slot #${slot.id}`)}))
					.filter((slot) => Number.isFinite(Number(slot.id)))
			: [];
		if (presetSlotsFromObjects.length > 0) {
			setSelectedSlots(presetSlotsFromObjects);
		} else {
			setSelectedSlots(
				presetSlotIds
					.map((id) => Number(id))
					.filter((id) => Number.isFinite(id))
					.map((id) => ({id, name: `Slot #${id}`}))
			);
		}
		setSlotSearch("");
		setIsSlotLoading(false);
		setIsSlotDropdownOpen(false);
		setTitle(initialValues?.title || "");
		setDestinationUrl(toStringOr(initialValues?.destination_url, ""));
		setCoverFile(null);
		setCoverName("");
		if (coverPreviewObjectUrlRef.current) URL.revokeObjectURL(coverPreviewObjectUrlRef.current);
		coverPreviewObjectUrlRef.current = "";
		setCoverPreviewUrl(initialValues?.cover_url || "");
		setPriority(toStringOr(initialValues?.priority, "10"));
		setSortOrder(toStringOr(initialValues?.sort_order, "1"));
		setBudgetImpressions(toStringOr(initialValues?.budget_impressions, ""));
		setBudgetClicks(toStringOr(initialValues?.budget_clicks, ""));
		setIsActive(initialValues?.active ?? initialValues?.is_active ?? true);
		setFieldErrors({});
	}, [isOpen, initialValues]);

	useEffect(() => () => {
		if (coverPreviewObjectUrlRef.current) URL.revokeObjectURL(coverPreviewObjectUrlRef.current);
	}, []);

	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (event) => {
			if (studioDropdownRef.current && !studioDropdownRef.current.contains(event.target)) {
				setIsStudioDropdownOpen(false);
			}
			if (slotDropdownRef.current && !slotDropdownRef.current.contains(event.target)) {
				setIsSlotDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;

		const timeoutId = setTimeout(async () => {
			setIsStudioSearching(true);
			try {
				const res = await API.get("admin/studios", {
					params: {
						approved: 1,
						search: studioSearch.trim(),
						items_per_page: 8,
					},
				});
				setStudios(normalizeList(res.data));
			} catch (e) {
				setStudios([]);
			} finally {
				setIsStudioSearching(false);
			}
		}, 250);

		return () => clearTimeout(timeoutId);
	}, [isOpen, studioSearch]);

	useEffect(() => {
		if (!isOpen) return;

		const loadSlots = async () => {
			setIsSlotLoading(true);
			try {
				const res = await API.get("admin/studio-ads/slots");
				setSlots(normalizeList(res.data));
			} catch (e) {
				setSlots([]);
			} finally {
				setIsSlotLoading(false);
			}
		};

		loadSlots();
	}, [isOpen]);

	if (!isOpen) return null;

	const getFieldError = (fieldNames) => {
		const names = Array.isArray(fieldNames) ? fieldNames : [fieldNames];
		for (const fieldName of names) {
			if (fieldErrors[fieldName]) return fieldErrors[fieldName];
			const serverFieldErrors = serverErrors?.[fieldName];
			if (Array.isArray(serverFieldErrors) && serverFieldErrors.length > 0) {
				return serverFieldErrors[0];
			}
		}
		return "";
	};

	const clearFieldError = (fieldName) => {
		setFieldErrors((prev) => {
			if (!prev[fieldName]) return prev;
			const next = {...prev};
			delete next[fieldName];
			return next;
		});
		if (onClearServerErrors) onClearServerErrors();
	};

	const isSlotSelected = (slotId) => selectedSlots.some((slot) => Number(slot.id) === Number(slotId));

	const toggleSlotSelection = (slot) => {
		setSelectedSlots((prev) => {
			const exists = prev.some((current) => Number(current.id) === Number(slot.id));
			if (exists) return prev.filter((current) => Number(current.id) !== Number(slot.id));
			return [...prev, {id: slot.id, name: firstNonEmpty(slot.name, slot.key, `Slot #${slot.id}`)}];
		});
		clearFieldError("slot_ids");
		clearFieldError("slot_id");
	};

	const removeSlotSelection = (slotId) => {
		setSelectedSlots((prev) => prev.filter((slot) => Number(slot.id) !== Number(slotId)));
		clearFieldError("slot_ids");
		clearFieldError("slot_id");
	};

	const filteredSlots = slots.filter((slot) => {
		if (!slotSearch.trim()) return true;
		const q = slotSearch.trim().toLowerCase();
		const name = (slot.name || "").toLowerCase();
		const key = (slot.key || "").toLowerCase();
		return name.includes(q) || key.includes(q) || String(slot.id).includes(q);
	});

	const handleSubmit = async () => {
		const nextErrors = {};
		if (selectedSlots.length === 0) nextErrors.slot_ids = "At least one ad slot is required.";
		if (!title.trim()) nextErrors.title = "Title is required.";
		if (!selectedStudio?.id && !destinationUrl.trim()) nextErrors.destination_url = "Destination URL is required when no studio is selected.";
		if (requireCover && !coverFile) {
			nextErrors.cover = "Cover image is required.";
		}
		if (coverFile) {
			const lowerName = (coverFile.name || "").toLowerCase();
			const validMime = ["image/png", "image/jpeg"].includes(coverFile.type);
			const validExtension = lowerName.endsWith(".png") || lowerName.endsWith(".jpg") || lowerName.endsWith(".jpeg");
			if (!validMime && !validExtension) {
				nextErrors.cover = "Only PNG or JPG images are allowed.";
			}
		}
		if (Object.keys(nextErrors).length > 0) {
			setFieldErrors(nextErrors);
			return;
		}

		setFieldErrors({});
		if (onClearServerErrors) onClearServerErrors();
		await onSubmit({
			id: initialValues?.id,
			studio_id: selectedStudio?.id ?? null,
			slot_ids: selectedSlots.map((slot) => Number(slot.id)),
			slot_id: Number(selectedSlots[0].id),
			title: title.trim(),
			destination_url: destinationUrl.trim(),
			cover: coverFile || null,
			priority: priority.trim(),
			sort_order: sortOrder.trim(),
			budget_impressions: budgetImpressions.trim(),
			budget_clicks: budgetClicks.trim(),
			active: isActive,
		});
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(0,0,0,0.45)] px-4">
			<div className="max-h-[90vh] w-full max-w-[760px] overflow-y-auto rounded-xl bg-white p-6 shadow-[0_12px_32px_rgba(0,0,0,0.15)]">
					<div className="mb-5 flex items-start justify-between">
						<div>
							<h3 className="text-2xl font-semibold text-[#1E1F21]">{modalTitle}</h3>
							<p className="mt-1 text-sm text-[#6D6E6F]">{modalDescription}</p>
						</div>
					<button
						type="button"
						onClick={onClose}
						disabled={isSaving}
						className="rounded-md px-2 py-1 text-[#6D6E6F] hover:bg-[#F4F4F5] disabled:opacity-60"
					>
						✕
					</button>
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div ref={studioDropdownRef} className="relative">
						<label className="mb-2 block text-sm font-medium text-[#1E1F21]">Studio</label>
						<input
							value={studioSearch}
							onFocus={() => setIsStudioDropdownOpen(true)}
							onChange={(e) => {
								setStudioSearch(e.target.value);
								setSelectedStudio(null);
								setIsStudioDropdownOpen(true);
								clearFieldError("studio_id");
							}}
							placeholder="Search studio..."
							className={`h-11 w-full rounded-md px-3 text-sm outline-none focus:border-[#31A762] ${
								getFieldError(["studio_id", "studio"]) ? "border border-[#DC3545]" : "border border-[#DADADA]"
							}`}
						/>
						<p className="mt-1 text-xs text-[#6D6E6F]">
							If no studio is selected, this ad is shown as a Brand ad and opens the destination URL instead of a studio page.
						</p>
						{isStudioDropdownOpen ? (
							<div className="absolute left-0 right-0 top-full z-30 mt-2 max-h-56 overflow-auto rounded-md border border-[#E4E1E0] bg-white shadow-[0_10px_24px_rgba(0,0,0,0.12)]">
								{isStudioSearching ? <p className="px-3 py-2 text-sm text-[#6D6E6F]">Searching...</p> : null}
								{!isStudioSearching && studios.length === 0 ? <p className="px-3 py-2 text-sm text-[#6D6E6F]">No studios found.</p> : null}
								{!isStudioSearching &&
									studios.map((studio) => (
										<button
											key={studio.id}
											type="button"
											onClick={() => {
												setSelectedStudio(studio);
												setStudioSearch(studio.name || `Studio #${studio.id}`);
												setIsStudioDropdownOpen(false);
												clearFieldError("studio_id");
											}}
											className="flex w-full items-center justify-between border-b border-[#F0F0F0] px-3 py-2 text-left last:border-b-0 hover:bg-[#F8F8F8]"
										>
											<div className="flex min-w-0 items-center gap-3">
												{studio.artwork_url ? (
													<img src={studio.artwork_url} alt={studio.name || `Studio ${studio.id}`} className="h-8 w-8 rounded-full object-cover" />
												) : (
													<div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EEF0F2] text-xs font-semibold text-[#6D6E6F]">
														{String(studio.name || "S").charAt(0).toUpperCase()}
													</div>
												)}
												<span className="truncate text-sm text-[#1E1F21]">{studio.name || `Studio #${studio.id}`}</span>
											</div>
											<span className="text-xs text-[#909090]">#{studio.id}</span>
										</button>
									))}
							</div>
						) : null}
						{getFieldError(["studio_id", "studio"]) ? <p className="mt-1 text-xs text-[#DC3545]">{getFieldError(["studio_id", "studio"])}</p> : null}
					</div>

					<div ref={slotDropdownRef} className="relative">
						<label className="mb-2 block text-sm font-medium text-[#1E1F21]">Ad Slots</label>
						<div
							className={`min-h-11 w-full rounded-md px-2 py-2 ${
								getFieldError(["slot_ids", "slot_id", "ad_slot_id", "slot"]) ? "border border-[#DC3545]" : "border border-[#DADADA]"
							}`}
						>
							<div className="flex flex-wrap items-center gap-2">
								{selectedSlots.map((slot) => (
									<span key={slot.id} className="inline-flex items-center gap-1 rounded-full bg-[#EEF9F2] px-3 py-1 text-xs font-medium text-[#198754]">
										{slot.name}
										<button
											type="button"
											onClick={() => removeSlotSelection(slot.id)}
											className="ml-1 text-[#198754] hover:text-[#0F5132]"
										>
											✕
										</button>
									</span>
								))}
								<input
									value={slotSearch}
									onFocus={() => setIsSlotDropdownOpen(true)}
									onChange={(e) => {
										setSlotSearch(e.target.value);
										setIsSlotDropdownOpen(true);
										clearFieldError("slot_ids");
									}}
									placeholder={selectedSlots.length ? "Add more slots..." : "Search and select ad slots..."}
									className="h-7 min-w-[180px] flex-1 border-0 px-1 text-sm outline-none"
								/>
							</div>
						</div>
						{isSlotDropdownOpen ? (
							<div className="absolute left-0 right-0 top-full z-30 mt-2 max-h-56 overflow-auto rounded-md border border-[#E4E1E0] bg-white shadow-[0_10px_24px_rgba(0,0,0,0.12)]">
								{isSlotLoading ? <p className="px-3 py-2 text-sm text-[#6D6E6F]">Loading slots...</p> : null}
								{!isSlotLoading && filteredSlots.length === 0 ? <p className="px-3 py-2 text-sm text-[#6D6E6F]">No slots found.</p> : null}
								{!isSlotLoading &&
									filteredSlots.map((slot) => (
										<button
											key={slot.id}
											type="button"
											onClick={() => {
												toggleSlotSelection(slot);
											}}
											className="flex w-full items-center justify-between border-b border-[#F0F0F0] px-3 py-2 text-left last:border-b-0 hover:bg-[#F8F8F8]"
										>
											<div className="min-w-0">
												<p className="truncate text-sm text-[#1E1F21]">{slot.name || `Slot #${slot.id}`}</p>
												<p className="truncate text-xs text-[#909090]">{slot.key || "No key"}</p>
											</div>
											<div className="flex items-center gap-2">
												<span className="text-xs text-[#909090]">#{slot.id}</span>
												<span className={`flex h-4 w-4 items-center justify-center rounded border text-[10px] ${
													isSlotSelected(slot.id) ? "border-[#31A762] bg-[#31A762] text-white" : "border-[#CFCFCF] text-transparent"
												}`}>
													✓
												</span>
											</div>
										</button>
									))}
							</div>
						) : null}
						{getFieldError(["slot_ids", "slot_id", "ad_slot_id", "slot"]) ? <p className="mt-1 text-xs text-[#DC3545]">{getFieldError(["slot_ids", "slot_id", "ad_slot_id", "slot"])}</p> : null}
					</div>
				</div>

				<div className="mt-4 space-y-4">
					<div>
						<label className="mb-2 block text-sm font-medium text-[#1E1F21]">Title</label>
						<input
							value={title}
							onChange={(e) => {
								setTitle(e.target.value);
								clearFieldError("title");
							}}
							className={`h-11 w-full rounded-md px-3 text-sm outline-none focus:border-[#31A762] ${
								getFieldError("title") ? "border border-[#DC3545]" : "border border-[#DADADA]"
							}`}
						/>
						{getFieldError("title") ? <p className="mt-1 text-xs text-[#DC3545]">{getFieldError("title")}</p> : null}
					</div>
					<div>
						<label className="mb-2 block text-sm font-medium text-[#1E1F21]">Destination URL</label>
						<input
							value={destinationUrl}
							onChange={(e) => {
								setDestinationUrl(e.target.value);
								clearFieldError("destination_url");
							}}
							placeholder="https://example.com"
							className={`h-11 w-full rounded-md px-3 text-sm outline-none focus:border-[#31A762] ${
								getFieldError("destination_url") ? "border border-[#DC3545]" : "border border-[#DADADA]"
							}`}
						/>
						{getFieldError("destination_url") ? <p className="mt-1 text-xs text-[#DC3545]">{getFieldError("destination_url")}</p> : null}
					</div>
					<div>
						<label className="mb-2 block text-sm font-medium text-[#1E1F21]">Cover (PNG/JPG)</label>
						<div className={`w-full rounded-md px-2 py-2 ${getFieldError("cover") ? "border border-[#DC3545]" : "border border-[#DADADA]"}`}>
							<div className="flex items-center gap-2">
								<label className="inline-flex cursor-pointer items-center rounded bg-[#EEF0F2] px-3 py-1.5 text-sm font-medium text-[#1E1F21] hover:bg-[#E5E7EB]">
									<input
										type="file"
										accept=".png,.jpg,.jpeg,image/png,image/jpeg"
										className="hidden"
										onChange={(e) => {
											const file = e.target.files?.[0] || null;
											setCoverFile(file);
											setCoverName(file ? file.name : "");
											if (coverPreviewObjectUrlRef.current) URL.revokeObjectURL(coverPreviewObjectUrlRef.current);
											if (file) {
												const nextPreview = URL.createObjectURL(file);
												coverPreviewObjectUrlRef.current = nextPreview;
												setCoverPreviewUrl(nextPreview);
											} else {
												coverPreviewObjectUrlRef.current = "";
												setCoverPreviewUrl(initialValues?.cover_url || "");
											}
											clearFieldError("cover");
										}}
									/>
									Choose image
								</label>
								<span className={`min-w-0 flex-1 truncate text-sm ${coverName ? "text-[#1E1F21]" : "text-[#6D6E6F]"}`}>
									{coverName || "PNG or JPG image"}
								</span>
							</div>
						</div>
						{getFieldError("cover") ? (
							<p className="mt-1 text-xs text-[#DC3545]">{getFieldError("cover")}</p>
						) : null}
						<p className="mt-1 text-xs text-[#6D6E6F]">Best display: 16:9 ratio, minimum width 1024px.</p>
							{coverPreviewUrl ? (
								<div className="mt-2 overflow-hidden rounded-md border border-[#E4E1E0] bg-[#F8F8F8]">
									<img src={coverPreviewUrl} alt="Ad cover preview" className="h-36 w-full object-cover" />
								</div>
							) : null}
							{!requireCover ? <p className="mt-1 text-xs text-[#6D6E6F]">Leave empty to keep current cover.</p> : null}
						</div>
					</div>

					<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-1">
						<div>
							<label className="mb-2 block text-sm font-medium text-[#1E1F21]">Priority</label>
							<input
							value={priority}
							onChange={(e) => {
								setPriority(e.target.value);
								clearFieldError("priority");
							}}
							className={`h-11 w-full rounded-md px-3 text-sm outline-none focus:border-[#31A762] ${
								getFieldError("priority") ? "border border-[#DC3545]" : "border border-[#DADADA]"
							}`}
								/>
								{getFieldError("priority") ? <p className="mt-1 text-xs text-[#DC3545]">{getFieldError("priority")}</p> : null}
							</div>
					</div>

				<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
					<div>
						<label className="mb-2 block text-sm font-medium text-[#1E1F21]">Budget impressions</label>
						<input
							value={budgetImpressions}
							onChange={(e) => {
								setBudgetImpressions(e.target.value);
								clearFieldError("budget_impressions");
							}}
							placeholder="0"
							className={`h-11 w-full rounded-md px-3 text-sm outline-none focus:border-[#31A762] ${
								getFieldError("budget_impressions") ? "border border-[#DC3545]" : "border border-[#DADADA]"
							}`}
						/>
						{getFieldError("budget_impressions") ? <p className="mt-1 text-xs text-[#DC3545]">{getFieldError("budget_impressions")}</p> : null}
					</div>
					<div>
						<label className="mb-2 block text-sm font-medium text-[#1E1F21]">Budget clicks</label>
						<input
							value={budgetClicks}
							onChange={(e) => {
								setBudgetClicks(e.target.value);
								clearFieldError("budget_clicks");
							}}
							placeholder="0"
							className={`h-11 w-full rounded-md px-3 text-sm outline-none focus:border-[#31A762] ${
								getFieldError("budget_clicks") ? "border border-[#DC3545]" : "border border-[#DADADA]"
							}`}
						/>
						{getFieldError("budget_clicks") ? <p className="mt-1 text-xs text-[#DC3545]">{getFieldError("budget_clicks")}</p> : null}
					</div>
				</div>

				<div className="mt-4 flex items-center justify-between rounded-md border border-[#E4E1E0] px-3 py-2">
					<div>
						<p className="text-sm font-medium text-[#1E1F21]">Active</p>
						<p className="text-xs text-[#6D6E6F]">{isActive ? "Enabled" : "Disabled"}</p>
					</div>
					<button
						type="button"
						onClick={() => setIsActive((prev) => !prev)}
						className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${isActive ? "bg-[#31A762]" : "bg-[#D1D5DB]"}`}
						aria-pressed={isActive}
					>
						<span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${isActive ? "translate-x-6" : "translate-x-1"}`} />
					</button>
				</div>

				<div className="mt-6 flex justify-end gap-3">
					<button
						type="button"
						onClick={onClose}
						disabled={isSaving}
						className="rounded-md border border-[#D5D5D5] px-4 py-2 text-sm font-semibold text-[#1E1F21] disabled:opacity-60"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={handleSubmit}
						disabled={isSaving}
						className="rounded-md bg-[#31A762] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
					>
						{isSaving ? "Saving..." : submitLabel}
					</button>
				</div>
			</div>
		</div>
	);
}

export default AddStudioAdModal;
